import React, { useState, useCallback } from 'react';
import { UploadCloudIcon } from './icons/UploadCloudIcon';
import { FileTextIcon } from './icons/FileTextIcon';
import { PreparationWorkflow } from './PositioningSections';
import {
  DOCUMENT_PREPARE_CATEGORIES,
  HERO_HEADLINE,
  HERO_SUPPORT,
} from '../config/productCopy';

type InputMode = 'upload' | 'paste';

export type PrepareOptions = {
  documentCategory?: string;
};

interface InputSelectorProps {
  onFileSelect: (file: File, options?: PrepareOptions) => void;
  onFilesSelect?: (files: File[], options?: PrepareOptions) => void;
  onTextSubmit: (text: string, options?: PrepareOptions) => void;
  maxFiles?: number;
  batchEnabled?: boolean;
}

const MAX_CHAR_COUNT = 500000;

const InputSelector: React.FC<InputSelectorProps> = ({
  onFileSelect,
  onFilesSelect,
  onTextSubmit,
  maxFiles = 1,
  batchEnabled = false,
}) => {
  const [mode, setMode] = useState<InputMode>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [documentCategory, setDocumentCategory] = useState('');

  const canBatch = batchEnabled && maxFiles > 1;
  const prepareOptions = (): PrepareOptions =>
    documentCategory ? { documentCategory } : {};

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files).slice(0, maxFiles);

        if (canBatch && files.length > 1) {
          setSelectedFiles(files);
        } else {
          onFileSelect(files[0], prepareOptions());
        }
      }
    },
    [onFileSelect, maxFiles, canBatch, documentCategory]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files).slice(0, maxFiles);

      if (canBatch && files.length > 1) {
        setSelectedFiles(files);
      } else if (canBatch) {
        setSelectedFiles(files);
      } else {
        onFileSelect(files[0], prepareOptions());
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleProcessFiles = () => {
    const opts = prepareOptions();
    if (selectedFiles.length === 1) {
      onFileSelect(selectedFiles[0], opts);
    } else if (selectedFiles.length > 1 && onFilesSelect) {
      onFilesSelect(selectedFiles, opts);
    }
    setSelectedFiles([]);
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHAR_COUNT) {
      setPastedText(text);
    }
  };

  const handlePrepareClick = () => {
    if (pastedText.trim()) {
      onTextSubmit(pastedText.trim(), prepareOptions());
    }
  };

  const charCount = pastedText.length;
  const isOverLimit = charCount > MAX_CHAR_COUNT;
  const canPrepare = pastedText.trim().length > 0 && !isOverLimit;

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const categorySelect = (
    <div className="mb-6 text-left max-w-md mx-auto">
      <label
        htmlFor="document-prepare-category"
        className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
      >
        What type of document are you preparing?
      </label>
      <select
        id="document-prepare-category"
        value={documentCategory}
        onChange={(e) => setDocumentCategory(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
      >
        {DOCUMENT_PREPARE_CATEGORIES.map((opt) => (
          <option key={opt.id || 'none'} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Optional — helps improve preparation for educational workflows. No document content is stored with this choice.
      </p>
    </div>
  );

  return (
    <div className="w-full text-center p-8 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 mb-2">
        Prepare a document for AI use
      </p>
      <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">{HERO_HEADLINE}</h2>
      <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-2xl mx-auto">
        {HERO_SUPPORT}
        {canBatch && (
          <span className="block text-emerald-600 dark:text-emerald-400 text-sm mt-1">
            Pro feature: Prepare up to {maxFiles} files at once
          </span>
        )}
      </p>

      <PreparationWorkflow className="mb-8" />

      {categorySelect}

      {/* Mode Toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-700 p-1">
          <button
            onClick={() => setMode('upload')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              mode === 'upload'
                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <span className="flex items-center gap-2">
              <UploadCloudIcon className="w-4 h-4" />
              Upload File{canBatch ? 's' : ''}
            </span>
          </button>
          <button
            onClick={() => setMode('paste')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              mode === 'paste'
                ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <span className="flex items-center gap-2">
              <FileTextIcon className="w-4 h-4" />
              Paste Text
            </span>
          </button>
        </div>
      </div>

      {mode === 'upload' && (
        <>
          {selectedFiles.length === 0 ? (
            <>
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`relative flex flex-col items-center justify-center w-full p-10 border-2 border-dashed rounded-lg transition-colors duration-200 ${
                  isDragging
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-slate-300 dark:border-slate-600'
                }`}
              >
                <UploadCloudIcon className="w-16 h-16 text-slate-400 dark:text-slate-500 mb-4" />
                <p className="text-slate-600 dark:text-slate-300 font-semibold">
                  Drag & drop a document to prepare
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">or</p>
                <label
                  htmlFor="file-upload"
                  className="mt-4 cursor-pointer px-6 py-2 bg-green-600 text-white font-semibold rounded-md hover:bg-green-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                >
                  <span>Browse File{canBatch ? 's' : ''}</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept=".pdf,.docx"
                    onChange={handleFileChange}
                    multiple={canBatch}
                  />
                </label>
              </div>
              <div className="mt-6 text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center space-x-2">
                <FileTextIcon className="w-4 h-4" />
                <span>
                  Supports PDF and DOCX{canBatch ? ` (up to ${maxFiles} at once)` : ''} — IEPs, evaluations,
                  progress reports, and more
                </span>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Selected Files ({selectedFiles.length}/{maxFiles})
                  </h3>
                  <button
                    onClick={handleClearFiles}
                    className="text-sm text-slate-500 hover:text-red-500 dark:text-slate-400 dark:hover:text-red-400"
                  >
                    Clear all
                  </button>
                </div>
                <ul className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600"
                    >
                      <div className="flex items-center gap-3">
                        <FileTextIcon className="w-5 h-5 text-emerald-500" />
                        <div className="text-left">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate max-w-[200px]">
                            {file.name}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(index)}
                        className="p-1 text-slate-400 hover:text-red-500 dark:hover:text-red-400"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
                {selectedFiles.length < maxFiles && (
                  <label className="mt-3 block">
                    <span className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline cursor-pointer">
                      + Add more files
                    </span>
                    <input
                      type="file"
                      className="sr-only"
                      accept=".pdf,.docx"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          const newFiles = Array.from(e.target.files);
                          const totalFiles = [...selectedFiles, ...newFiles].slice(0, maxFiles);
                          setSelectedFiles(totalFiles);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              <button
                onClick={handleProcessFiles}
                className="w-full py-3 px-6 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-500/25"
              >
                Prepare {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''} for AI
              </button>
            </div>
          )}
        </>
      )}

      {mode === 'paste' && (
        <>
          <div className="relative">
            <textarea
              value={pastedText}
              onChange={handleTextChange}
              placeholder="Paste educational document text to prepare for AI tools…

(Use synthetic or de-identified examples when testing.)"
              className={`w-full h-64 p-4 text-sm text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-900 border-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors ${
                isOverLimit
                  ? 'border-red-400 dark:border-red-500'
                  : 'border-slate-200 dark:border-slate-600'
              }`}
            />

            <div
              className={`absolute bottom-3 right-3 text-xs px-2 py-1 rounded ${
                isOverLimit
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}
            >
              {charCount.toLocaleString()} / {MAX_CHAR_COUNT.toLocaleString()}
            </div>
          </div>

          {isOverLimit && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              This document is too large to process. Try splitting it into smaller sections.
            </p>
          )}

          <button
            onClick={handlePrepareClick}
            disabled={!canPrepare}
            className={`mt-4 w-full py-3 px-6 font-semibold rounded-lg transition-all ${
              canPrepare
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/25'
                : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            Prepare for AI
          </button>

          <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
            Your text is processed to create a prepared, redacted working copy — it is not stored by this
            app after processing.
          </p>
        </>
      )}
    </div>
  );
};

export default InputSelector;
